import { ThemeRadiusVariable, ThemeSpaceVariable, useDisclosure } from "@caspeco/casper-ui-library";
import { useBookingMenuRespond } from "api/api-hooks/use-booking-menu-respond";
import { useCreateBooking } from "api/api-hooks/use-create-booking";
import { useUpdateBooking } from "api/api-hooks/use-update-booking";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { BookableItem } from "components/booking-modal/bookable-item";
import BookingModalBody from "components/booking-modal/booking-modal-body";
import { BookingModalFooter } from "components/booking-modal/booking-modal-footer";
import { ModalSelectionSearch } from "components/booking-modal/modal-selection-search";
import { ExperienceViewFilterModal } from "components/pages/experience-view/experience-view-filter-modal";
import CustomizeWizardModalBody from "components/selection-components/customize-wizard/customize-wizard-modal-body-wrapper";
import PreorderBody from "components/selection-components/preorder/preorder-body";
import { isSameWaitListSlot } from "components/selection-components/select-activity/wait-list-utils";
import { useOriginalSelectedTime } from "components/selection-components/select-activity/use-original-selected-time";
import { useRemoveActivity } from "components/selection-components/summary/use-remove-activity";
import { SlideDirection, SlideModal } from "components/slide-modal/slide-modal";
import { ANIMATION_DURATION } from "components/use-slide-modal";
import { PreorderContext } from "context/preorder-context";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import { saveSectionNameAndWebRuleTitle } from "logger/event-utils";
import { error as logError } from "logger/logger";
import { useStepTracking } from "logger/use-step-tracking";
import { TrackingStepName } from "logger/user-tracking";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { urlBuilder } from "search-params/url-builder";
import { Time } from "store/store-types";
import { ImageServiceAspectRatio } from "types/image-service-aspect-ratio";
import { BookingMenu, MenuStatus } from "types/menu";
import { TimeSlot } from "types/time-slot";

function resolveChainTimes(item: BookableItem, selectedTime: TimeSlot): Time[] {
    if (item.chains) {
        const chain = item.chains.find((c) => c[0].start.isSame(selectedTime.start));
        return chain ? chain.map((slot) => ({ ruleId: slot.ruleId, start: slot.start })) : [];
    }
    return [{ ruleId: selectedTime.ruleId, start: selectedTime.start }];
}

interface BookingModalProps {
    item: BookableItem | null;
    isOpen: boolean;
    onClose: () => void;
    /** Activity wrapper provides this to handle the waitlist flow (clear dialog, booking creation). */
    onWaitlistContinue?: (times: TimeSlot[], close: () => void) => void;
    openOnMenuStep?: boolean;
}

// Extra time after the slide-out animation completes before resetting the direction.
// Must be long enough for the Chakra modal to fully unmount (which happens at ANIMATION_DURATION).
const DIRECTION_RESET_BUFFER = 180;

function BookingModal({
    item: itemProp,
    isOpen,
    onClose,
    onWaitlistContinue,
    openOnMenuStep,
}: BookingModalProps) {
    const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();
    const [slideDirection, setSlideDirection] = useState<SlideDirection>("right");
    const [selectedTime, setSelectedTime] = useState<TimeSlot | undefined>(undefined);
    const [selectedWaitListTimes, setSelectedWaitListTimes] = useState<TimeSlot[]>([]);
    const [step, setStep] = useState(0);
    const directionResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // itemProp goes null at the same time isOpen goes false (parents derive both from the
    // same state), so we freeze the last non-null value to keep content visible during
    // the slide-out animation.
    const [item, setItem] = useState(itemProp);

    useEffect(() => {
        if (itemProp !== null) {
            setItem(itemProp);
        }
    }, [itemProp]);

    const state = useBookingStateContext();
    const { setState } = useBookingActionsContext();
    const { date, isBookingRequest } = state;
    const { data: booking } = useWebBooking();
    const createBooking = useCreateBooking();
    const updateBooking = useUpdateBooking();

    const preorderContext = useContext(PreorderContext);
    const { currentQuantities, currentTotalQuantity, setSelectedArticleHasMandatoryMenu } =
        preorderContext || {};
    const latestCurrentQuantities = useRef(currentQuantities);
    useEffect(() => {
        latestCurrentQuantities.current = currentQuantities;
    }, [currentQuantities]);
    const updateBookingMenu = useBookingMenuRespond();

    const displaySection = item?.activities[0] ?? null;
    const originalTime = useOriginalSelectedTime(displaySection);
    const isMultiActivity = (item?.activities.length ?? 0) > 1;

    // For multi-activity experiences, collect the rule IDs of all sibling activities
    // so that the user's own booking doesn't block time slot selection.
    const ignoredRuleIds = useMemo(() => {
        if (!isMultiActivity || !booking) return undefined;
        const sectionIds = new Set(item?.activities.map((a) => a.id));
        const ruleIds = booking.articles
            .filter((a) => sectionIds?.has(a.sectionId))
            .map((a) => a.ruleId);
        return ruleIds.length > 0 ? new Set(ruleIds) : undefined;
    }, [isMultiActivity, booking, item?.activities]);

    // For multi-activity experiences, check if any chain's later activities would
    // conflict with other (non-experience) bookings
    const chainConflictStartTimes = useMemo((): Set<string> | undefined => {
        if (!isMultiActivity || !booking || !item?.chains || item.chains.length === 0)
            return undefined;
        const chains = item.chains;

        const firstRuleId = displaySection?.timeSets[0]?.id;
        const otherArticles = booking.articles.filter(
            (a) => !ignoredRuleIds?.has(a.ruleId) && a.ruleId !== firstRuleId
        );
        if (otherArticles.length === 0) return undefined;

        const conflicting = new Set<string>();
        for (const chain of chains) {
            const hasConflict = chain.some((slot: TimeSlot) =>
                otherArticles.some(
                    (article) => slot.start.isBefore(article.end) && slot.end.isAfter(article.start)
                )
            );
            if (hasConflict) {
                conflicting.add(chain[0].start.format("HH:mm"));
            }
        }
        return conflicting.size > 0 ? conflicting : undefined;
    }, [isMultiActivity, booking, item?.chains, ignoredRuleIds, displaySection]);
    const { removeActivities } = useRemoveActivity();

    // Keep a ref so the pre-selection effect can read the latest originalTime without
    // including it as a dependency — preventing re-fires when only the reference changes.
    const originalTimeRef = useRef(originalTime);
    originalTimeRef.current = originalTime;

    useStepTracking({
        stepName: item?.stepName ?? TrackingStepName.ActivityModal,
        metadata: item?.trackingMetadata,
        autoTrack: isOpen && !!item,
        dependencies: item?.trackingDependencies ?? [],
    });

    useEffect(() => {
        setSelectedTime(undefined);
    }, [date]);

    // Swapping onto a suggested experience keeps the modal mounted, so the previous one's
    // waitlist selection would otherwise survive into a footer that no longer matches it.
    useEffect(() => {
        setSelectedWaitListTimes([]);
    }, [item?.id]);

    // Pre-select the already-booked time when the booking loads or the modal switches items.
    // Using a ref for originalTime so a reference change alone never re-triggers this effect
    // and causes an in-progress deselection to be undone.
    useEffect(() => {
        if (booking && originalTimeRef.current) {
            setSelectedTime(originalTimeRef.current);
        }
    }, [booking, item?.id]);

    useEffect(() => {
        if (isOpen) {
            setSlideDirection("right");
        }
    }, [isOpen]);

    // Jump straight to the menu step with the booked time applied. Applied once per opening
    // (latched by the ref) so pressing back inside the modal still returns to time selection
    // instead of being bounced forward again.
    const hasOpenedOnMenuStep = useRef(false);
    useEffect(() => {
        if (!isOpen) {
            hasOpenedOnMenuStep.current = false;
            return;
        }
        if (!openOnMenuStep || hasOpenedOnMenuStep.current || !originalTime) return;

        hasOpenedOnMenuStep.current = true;
        setSelectedTime(originalTime);
        setStep(1);
        setSelectedArticleHasMandatoryMenu?.(true);
    }, [isOpen, openOnMenuStep, originalTime, setSelectedArticleHasMandatoryMenu]);

    const closeWithAnimation = (direction: SlideDirection = "right", cleanup?: () => void) => {
        if (directionResetTimerRef.current !== null) {
            clearTimeout(directionResetTimerRef.current);
        }
        setSlideDirection(direction);
        requestAnimationFrame(() => {
            onClose();
            setTimeout(() => {
                setSelectedTime(undefined);
                setStep(0);
                cleanup?.();
            }, ANIMATION_DURATION);
            directionResetTimerRef.current = setTimeout(() => {
                directionResetTimerRef.current = null;
                setSlideDirection("right");
            }, ANIMATION_DURATION + DIRECTION_RESET_BUFFER);
        });
    };

    const handleSelectTime = (time: TimeSlot) => {
        if (selectedWaitListTimes.length > 0) setSelectedWaitListTimes([]);
        setState?.((prev) => ({ ...prev, isWaitList: false }));
        setSelectedTime((prev) => (prev?.start.isSame(time.start) ? undefined : time));
    };

    const setWaitListTime = (time: TimeSlot) => {
        if (selectedTime) setSelectedTime(undefined);
        setState?.((prev) => ({ ...prev, isWaitList: true }));
        const idx = selectedWaitListTimes.findIndex((t) => isSameWaitListSlot(t, time));
        setSelectedWaitListTimes(
            idx > -1
                ? selectedWaitListTimes.filter((_, i) => i !== idx)
                : [...selectedWaitListTimes, time]
        );
    };

    const handleRemove = async () => {
        if (!item) return;
        const activitiesToRemove = item.activities
            .map((section) => {
                const bookedTime = booking?.getBookTimes().find((t) => t.sectionId === section.id);
                return bookedTime ? { ruleId: bookedTime.ruleId, sectionId: section.id } : null;
            })
            .filter((a): a is { ruleId: number; sectionId: number } => a !== null);
        await removeActivities(activitiesToRemove);
    };

    const onContinue = async () => {
        const shouldRemoveActivity = !!(originalTime && !selectedTime);
        if (shouldRemoveActivity) {
            await handleRemove();
            closeWithAnimation("right");
            return;
        }
        if (!selectedTime || !item) return;

        const times = resolveChainTimes(item, selectedTime);
        if (times.length === 0) {
            logError("Failed to build booking times", { itemId: item.id });
            return;
        }

        saveSectionNameAndWebRuleTitle(selectedTime.name, selectedTime.title);
        const stateWithTimes = { ...state, times };
        setState?.(stateWithTimes);

        let bookingResult;
        if (booking) {
            bookingResult = await updateBooking.mutateAsync({
                state: stateWithTimes,
                timesToUpdate: times,
                isInquiry: isBookingRequest,
            });
        } else {
            bookingResult = await createBooking.mutateAsync({
                state: stateWithTimes,
                isInquiry: isBookingRequest,
            });
        }

        const updatedArticle = bookingResult?.articles?.find((a) =>
            selectedTime.start.isSame(a.start)
        );
        const articleHasMenu = updatedArticle?.menuGuid && updatedArticle.menuGuid !== "";
        const menu = bookingResult?.menus?.find(
            (m) => m.publicId === updatedArticle?.menuGuid
        )?.groups;
        const menuHasItems = menu?.some((g) => g.items && g.items.length > 0);
        const menuRule = bookingResult?.webBookingRules?.find(
            (r) => r.id === updatedArticle?.ruleId
        );
        setSelectedArticleHasMandatoryMenu?.(!!menuRule?.requireMenuSelection);

        if (articleHasMenu && menuHasItems) {
            setStep(1);
        } else {
            closeWithAnimation("bottom", item.onAfterConfirm);
        }
    };

    const onPreorderContinue = async () => {
        const closeForward = () => closeWithAnimation("bottom", item?.onAfterConfirm);
        if (currentTotalQuantity && currentTotalQuantity > 0) {
            const currentActivity = booking?.articles.find((a) =>
                a.start.isSame(selectedTime?.start)
            );
            const currentMenu = booking?.menus?.find(
                (m) => m.status === MenuStatus.Sent && m.publicId === currentActivity?.menuGuid
            );
            if (!currentMenu?.groups || !latestCurrentQuantities.current) {
                return closeForward();
            }
            const bookingMenuWithChoices: BookingMenu = {
                ...currentMenu,
                groups: currentMenu.groups.map((group, groupIndex) => ({
                    ...group,
                    items:
                        group.items?.map((item, itemIndex) => ({
                            ...item,
                            quantity:
                                latestCurrentQuantities.current?.[groupIndex]?.[itemIndex] || 0,
                        })) || [],
                })),
            };
            await updateBookingMenu.mutateAsync({ bookingMenu: bookingMenuWithChoices });
        }
        closeForward();
    };

    const handleWaitlistContinue = () => {
        const closeFn = () => closeWithAnimation("right");
        onWaitlistContinue?.(selectedWaitListTimes, closeFn);
    };

    const imageParams = { AspectRatio: ImageServiceAspectRatio.SixteenByNine, Type: "webp" };
    const headerImage = item?.image
        ? urlBuilder(`${import.meta.env.VITE_APP_IMAGE_SERVICE_URL}/${item.image}`, imageParams)
        : undefined;
    // Reserved for the whole step, not just once times are picked: the footer grows by a line of
    // guidance when they are, and resizing the body mid-selection tips it into scrolling.
    const reservesWaitListFooterGuidance =
        step === 0 && item?.supportsWaitlist && !originalTime && !isBookingRequest;

    // Not on the menu step: changing the date there would strip a selection already made.
    const showsSelectionSearch = !!item?.showSelectionSearch && step === 0;

    const desktopHeight = `calc(100% - 2 * ${ThemeSpaceVariable.XLarge})`;
    const casperModalWidth = "35em";

    return (
        <SlideModal
            isOpen={isOpen}
            onClose={() => closeWithAnimation("right")}
            slideDirection={slideDirection}
            variant="centered"
            size="md"
            closeOnOverlayClick={step === 0}
            modalContentProps={{
                // dvh, not vh: iOS Safari resolves vh to the large viewport, leaving the bottom of
                // the modal off-screen with no scroll left to reach it.
                height: { base: "100dvh", md: desktopHeight },
                width: { base: "100%", md: casperModalWidth },
                maxW: { base: "none", md: casperModalWidth },
                ...(item?.dataTestId && { "data-testid": item.dataTestId }),
                borderRadius: { base: 0, md: ThemeRadiusVariable.Large },
                overflow: "hidden",
            }}
        >
            <CustomizeWizardModalBody
                onBack={step === 1 ? () => setStep(0) : undefined}
                headerImage={step === 0 ? headerImage : undefined}
                footerHeightSpacing={reservesWaitListFooterGuidance ? "132px" : undefined}
                headerToolbar={
                    showsSelectionSearch ? (
                        <ModalSelectionSearch onClick={onFilterOpen} />
                    ) : undefined
                }
                onStepBack={step === 0 ? item?.onReturnToOrigin : undefined}
                onDismiss={showsSelectionSearch ? () => closeWithAnimation("right") : undefined}
            >
                {step === 0 ? (
                    <BookingModalBody
                        key="wizardbody-0"
                        section={displaySection ?? undefined}
                        badgeSections={item?.activities}
                        maximumGapTime={item?.maximumGapTime}
                        name={item?.name ?? ""}
                        description={item?.description}
                        showStartTimeHeading={item?.showStartTimeHeading}
                        hideWebRuleHeader={item?.hideWebRuleHeader}
                        hasMenuChoice={item?.hasMenuChoice}
                        showAvailabilityBadge={item?.showAvailabilityBadge}
                        suggestions={item?.suggestions}
                        onSelectSuggestion={item?.onSelectSuggestion}
                        selectedTime={selectedTime}
                        setSelectedTime={handleSelectTime}
                        setSelectedWaitListTimes={
                            item?.supportsWaitlist ? setWaitListTime : () => {}
                        }
                        selectedWaitListTimes={item?.supportsWaitlist ? selectedWaitListTimes : []}
                        ignoredRuleIds={ignoredRuleIds}
                        chainConflictStartTimes={chainConflictStartTimes}
                    />
                ) : (
                    <PreorderBody
                        key="wizardbody-1"
                        selectedTime={selectedTime}
                        preorderHeaderName={item?.name}
                    />
                )}
            </CustomizeWizardModalBody>
            <BookingModalFooter
                item={item}
                step={step}
                originalTime={originalTime}
                selectedTime={selectedTime}
                selectedWaitListTimes={selectedWaitListTimes}
                onContinue={onContinue}
                onPreorderContinue={onPreorderContinue}
                onWaitlistContinue={handleWaitlistContinue}
                onFilterOpen={onFilterOpen}
            />
            <ExperienceViewFilterModal
                isOpen={isFilterOpen}
                onClose={onFilterClose}
                sectionId={item?.filterSectionId}
                experienceId={item?.filterExperienceId}
            />
        </SlideModal>
    );
}

export { BookingModal };
